import { Components, Theme } from '@mui/material'
import { CheckCircleOMIcon, ErrorOMIcon, InfoOMIcon, WarningOMIcon } from '@omnea-app/icons'

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    neutral: true
    'error-strong': true
    // TODO - uncomment when error severity removed
    // error: false
  }

  interface AlertPropsVariantOverrides {
    filled: false
    outlined: false
  }
}

// Text buttons in the action get custom styles per severity
const actionButtonSelector = '& .MuiAlert-action .MuiButton-root.MuiButton-text.MuiButton-colorSecondary'

const CONTENT_HEIGHT = '28px'
const HALF_CONTENT_HEIGHT = '14px'

export const MuiAlert: Components<Omit<Theme, 'components'>>['MuiAlert'] = {
  defaultProps: {
    iconMapping: {
      neutral: <InfoOMIcon />,
      success: <CheckCircleOMIcon />,
      warning: <WarningOMIcon />,
      error: <ErrorOMIcon />,
      'error-strong': <ErrorOMIcon />,
      info: <InfoOMIcon />,
    },
    slotProps: {
      // Most actions are centred, but override for the close button
      closeButton: {
        sx: {
          alignSelf: 'flex-start',
        },
      },
    },
  },
  styleOverrides: {
    icon: ({ theme }) => ({
      marginRight: theme.spacing(2.5),
      padding: 0,
      position: 'relative',

      '& > svg': {
        top: HALF_CONTENT_HEIGHT,
        transform: 'translateY(-50%)',
        position: 'relative',
      },
      // paddingTop: theme.spacing(1),
      '& > svg:not(.MuiSvgIcon-root)': {
        width: '20px',
        height: '20px',
      },
      '& > svg.MuiSvgIcon-root': {
        width: '20px',
      },
    }),
    action: ({ theme }) => ({
      marginRight: 0,
      // Ensure close action is top aligned without spacing
      paddingTop: theme.spacing(0),
      // Most actions are centred, close button is overridden above
      alignItems: 'center',
    }),
    message: ({ theme }) => ({
      flex: 1,
      padding: 0,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      alignSelf: 'center',
      // if the message has buttons or alert titles it ruins the horizontal alignment, this is just to correct that
      '&:has( > .MuiButton-root)': {
        paddingTop: 0,
        paddingBottom: 0,
      },
      '&:has(.MuiAlertTitle-root)': {
        ...theme.typography['small-regular'],
      },
      '& .MuiAlertTitle-root': {
        ...theme.typography['body-medium'],
        marginTop: theme.spacing(0),
      },
    }),
    root: ({ theme }) => ({
      padding: theme.spacing(2, 2, 2, 4),
      '&:has(> .MuiAlert-action:not(:empty))': {
        '& .MuiAlert-message': {
          minWidth: 0,
          overflowWrap: 'anywhere',
        },
        '& .MuiAlert-action': {
          flexShrink: 0,
          '& .MuiButton-root': {
            whiteSpace: 'nowrap',
          },
          [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
          },
        },
      },
      '&:not(:has(> .MuiAlert-action:not(:empty)))': {
        paddingRight: theme.spacing(4),
      },
      '& > .MuiAlert-action:empty': {
        display: 'none',
      },
      borderRadius: theme.shape['radius-lg'],
      borderStyle: 'solid',
      borderWidth: theme.strokeWidth.default,
      ...theme.typography['body-medium'],
      minHeight: CONTENT_HEIGHT,
      flexShrink: 0,
      '& pre': {
        padding: theme.spacing(2.5, 4),
        backgroundColor: theme.palette.bg.surface.inset,
        borderRadius: theme.shape['radius-md'],
        margin: 0,
        marginTop: theme.spacing(1),
        whiteSpace: 'pre-wrap',
      },
    }),
  },
  variants: [
    {
      props: { severity: 'neutral' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.surface.subtle,
        color: theme.palette.text.default,
        borderColor: theme.palette.border.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.default,
        },
        // neutral severity button just uses standard secondary text
      }),
    },
    {
      props: { severity: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.info.muted,
        color: theme.palette.text.info.onInfoMuted,
        borderColor: theme.palette.border.info.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.info.onInfoMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.info.onInfoMuted,
        },
      }),
    },
    {
      props: { severity: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.success.muted,
        color: theme.palette.text.success.onSuccessMuted,
        borderColor: theme.palette.border.success.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.success.onSuccessMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.success.onSuccessMuted,
        },
      }),
    },
    {
      props: { severity: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.warning.muted,
        color: theme.palette.text.warning.onWarningMuted,
        borderColor: theme.palette.border.warning.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.warning.onWarningMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.warning.onWarningMuted,
        },
      }),
    },
    {
      props: { severity: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.muted,
        color: theme.palette.text.danger.onDangerMuted,
        borderColor: theme.palette.border.danger.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.danger.onDangerMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.danger.onDangerMuted,
        },
      }),
    },
    {
      props: { severity: 'error-strong' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        color: theme.palette.text.danger.onDanger,
        borderColor: theme.palette.bg.danger.default,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.danger.onDanger,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
  ],
}
